import React from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';

import {
    Spacer,
    TextButton,
    Text,
} from '@zillow/constellation';

import TableMFH from 'app/shared/components/TableMFH';
import AddressColumn from 'app/shared/components/Analytics/AddressColumn';
import HeaderTooltip from 'app/shared/components/Analytics/HeaderTooltip';

import AnalyticsActions from 'app/shared/actions/AnalyticsActions';
import ModalActions from 'app/shared/actions/ModalActions';

import ModalConstants from 'app/shared/constants/ModalConstants';
import AdproductsConstants from 'app/shared/constants/AdproductsConstants';
import numberUtils from 'app/shared/utils/numberUtils';

import adproductsUtils from 'app/shared/utils/adproductsUtils';

const propTypes = {
    coreProductInvoiceModel: PropTypes.object.isRequired,
    isScreenMobile: PropTypes.bool.isRequired,
    isLoading: PropTypes.bool.isRequired,
    onPropertyClick: PropTypes.func.isRequired,
};

const containsPackageProducts = (model) => {
    if (!model || !model.listingInvoices || !model.listingInvoices.length) {
        return false;
    }
    return Object.values(model.listingInvoices)
        .map(invoice => invoice.adproductTypes)
        .filter(productTypes => productTypes !== undefined)
        .flatMap(productTypes => productTypes)
        .find(productType => Boolean(AdproductsConstants.PackagePlatforms[productType]));
};

function Error({ message }) {
    return (
        <Spacer paddingY="sm">
            <Text as="div" fontType="finePrint">
                {message}
            </Text>
        </Spacer>
    );
}

const AllSpendTable = ({
    onPropertyClick,
    isScreenMobile,
    coreProductInvoiceModel,
    isLoading,
    error,
}) => {
    const dispatch = useDispatch();

    const getCostWithDefault = (cost) => {
        return numberUtils.asCurrencyWithDefault(cost, '-');
    };

    const getTotal = (total) => {
        return total !== undefined ? total : '-';
    };

    const addressColumnRenderer = data => (
        <AddressColumn
            index={data.index}
            listing={data.listing}
            listingKey={data.listingKey}
            onClick={data.listing.hasAccess ? onPropertyClick : undefined}
            useAddress={data.activeDays > 0}
        />
    );

    const previewAd = (insertionOrderId) => {
        dispatch(AnalyticsActions.getBoostAdPreviews({ insertionOrderId }))
            .then((response) => {
                const header = response.propertyDetails.name;
                dispatch(ModalActions.showModal({
                    modalType: ModalConstants.BOOST_AD_PREVIEWS,
                    header,
                    boostAdPreviews: response,
                }));
            })
            .catch((error) => {
                dispatch(ModalActions.showModal({
                    modalType: ModalConstants.ERROR_MODAL,
                    header: 'Error',
                    error,
                }));
            });
    };

    const getRowList = (coreProductInvoiceModel, isMobile, hasLeadData,
        hasClickData, hasLeaseData, hasWaitlistData, hasBoostData) => {
        const invoices = Object.values(coreProductInvoiceModel.listingInvoices);
        const listings = coreProductInvoiceModel.propertyDetails;
        return invoices.map((invoice, index) => {
            const {
                feedListingId,
                feedId,
                numPhoneLeads,
                numEmailLeads,
                billableClickCount,
                leaseCount,
                coreProductCost,
                waitlistedDays,
                waitlistCost,
                boostCost,
                totalCost,
                isClickProduct,
                isLeadProduct,
                isLeaseProduct,
                isFlatFeeProduct,
                dailyRate,
                activeDays,
                unitName,
                adproductTypes,
                insertionOrderId,
            } = invoice;
            const listingKey = `${feedListingId}_${feedId}`;
            const listing = listings[listingKey] || {};
            const rowData = {
                listingKey,
                listing: { ...listing, unit: unitName },
                propertyName: activeDays > 0 ? listing.street : (listing.name || listing.street),
                index,
            };

            if (!isMobile) {
                if (hasLeadData) {
                    rowData.phoneLeads = (isLeadProduct || isFlatFeeProduct) ? numPhoneLeads : undefined;
                    rowData.emailLeads = (isLeadProduct || isFlatFeeProduct) ? numEmailLeads : undefined;
                }
                if (hasClickData) {
                    rowData.totalClicks = (isClickProduct || isFlatFeeProduct)
                        ? billableClickCount : undefined;
                }
                if (hasLeaseData) {
                    rowData.totalLeases = (isLeaseProduct || isFlatFeeProduct) ? leaseCount : undefined;
                }
                if (hasLeadData || hasClickData || hasLeaseData) {
                    rowData.coreProductCost = coreProductCost;
                }
                if (hasWaitlistData) {
                    rowData.waitlistedDays = waitlistedDays > 0 ? waitlistedDays : undefined;
                    rowData.waitlistCost = waitlistedDays > 0 ? waitlistCost : undefined;
                }
                if (hasBoostData) {
                    rowData.boostCost = boostCost;
                }
            }
            rowData.activeDays = activeDays;
            rowData.dailyRate = dailyRate;
            rowData.totalCost = totalCost;
            rowData.adproductTypes = adproductTypes;
            rowData.insertionOrderId = insertionOrderId;
            return rowData;
        });
    };

    const createColumnConfig = (hasLeadData, hasClickData, hasLeaseData, hasWaitlistData,
        hasBoostData, rocketshipAvailable, hasPackageProducts, containsOnlyPackageProducts) => {
        if (isScreenMobile) {
            return [
                {
                    dataKey: 'propertyName',
                    width: 9,
                    header: 'Property',
                    isSortable: true,
                    renderer: addressColumnRenderer,
                },
                {
                    dataKey: 'totalCost',
                    width: 3,
                    header: 'Total Cost',
                    align: 'right',
                    isSortable: true,
                    renderer: data => getCostWithDefault(data.totalCost),
                },
            ];
        }
        const columnConfig = [

        ];
        if (hasLeadData) {
            columnConfig.push({
                dataKey: 'phoneLeads',
                width: 1,
                header: 'Phone Contacts',
                align: 'right',
                isSortable: true,
                renderer: data => getTotal(data.phoneLeads),
            });
            columnConfig.push({
                dataKey: 'emailLeads',
                width: 1,
                header: 'Email Contacts',
                align: 'right',
                isSortable: true,
                renderer: data => getTotal(data.emailLeads),
            });
        }
        if (hasClickData) {
            columnConfig.push({
                dataKey: 'totalClicks',
                width: 1,
                header: 'Website Referrals',
                align: 'right',
                isSortable: true,
                renderer: data => getTotal(data.totalClicks),
            });
        }
        if (hasLeaseData) {
            columnConfig.push({
                dataKey: 'totalLeases',
                width: 1,
                header: 'Total Leases',
                align: 'right',
                isSortable: true,
                renderer: data => getTotal(data.totalLeases),
            });
        }
        if ((hasLeadData || hasClickData || hasLeaseData) && !containsOnlyPackageProducts) {
            columnConfig.push({
                width: 1,
                dataKey: 'coreProductCost',
                align: 'right',
                header: 'Contract Spend',
                headerRenderer: () => (
                    <HeaderTooltip
                        title="CONTRACT SPEND"
                        message="Excludes waitlist charges"
                    />
                ),
                renderer: data => getCostWithDefault(data.coreProductCost),
            });
        }
        if (hasWaitlistData) {
            columnConfig.push({
                dataKey: 'waitlistedDays',
                width: 1,
                header: 'Waitlist Days',
                align: 'right',
                isSortable: true,
                renderer: data => getTotal(data.waitlistedDays),
            });
            columnConfig.push({
                dataKey: 'waitlistCost',
                width: 1,
                header: 'Waitlist Cost',
                align: 'right',
                isSortable: true,
                renderer: data => getCostWithDefault(data.waitlistCost),
            });
        }
        if (hasBoostData && !containsOnlyPackageProducts) {
            columnConfig.push({
                dataKey: 'boostCost',
                width: 1,
                header: 'Boost Spend',
                align: 'right',
                isSortable: true,
                renderer: data => getCostWithDefault(data.boostCost),
            });
        }
        if (rocketshipAvailable) {
            columnConfig.push({
                dataKey: 'activeDays',
                width: 1,
                header: 'Active Days',
                align: 'right',
                isSortable: true,
                renderer: data => getTotal(data.activeDays),
            });
            columnConfig.push({
                dataKey: 'dailyRate',
                width: 1,
                header: 'Daily Rate',
                align: 'right',
                isSortable: true,
                renderer: data => getCostWithDefault(data.dailyRate),
            });
        }
        columnConfig.push({
            dataKey: 'totalCost',
            width: 1,
            headerRenderer: () => {
                if (!containsOnlyPackageProducts) {
                    return 'TOTAL COST';
                }
                return (
                    <HeaderTooltip
                        title="AD SPEND"
                        message="Your contracted monthly spend for advertising. Excludes waitlist charges."
                    />
                );
            },
            align: 'right',
            isSortable: true,
            renderer: data => getCostWithDefault(data.totalCost),
        });
        if (containsOnlyPackageProducts) {
            columnConfig.push({
                header: 'Preview',
                align: 'right',
                width: 1,
                renderer: (data) => {
                    if (!data.insertionOrderId) {
                        return '-';
                    }
                    return (
                        <TextButton fontType="bodySmallHeading" onClick={() => previewAd(data.insertionOrderId)}>
                            View Ad
                        </TextButton>
                    );
                },
            });
        }
        const totalWidth = Math.ceil((columnConfig.length + 1) / 0.8);
        const headerColWidth = totalWidth - columnConfig.length;

        if (hasPackageProducts) {
            columnConfig.unshift({
                header: 'Platforms',
                align: 'center',
                width: 1,
                renderer: (data) => {
                    if (!data.adproductTypes.length) {
                        return '-';
                    }
                    return data.adproductTypes
                        .map(productType => AdproductsConstants.PackagePlatforms[productType])
                        .find(platform => platform !== null) || '-';
                },
            });
        }
        columnConfig.unshift({
            header: 'Advertising Type',
            width: 1,
            renderer: data => (data.adproductTypes.length > 0
                ? data.adproductTypes.map((productType, index) => (
                    <Text key={index} fontType="finePrint">{productType}</Text>
                ))
                : '-'),
        });

        columnConfig.unshift({
            dataKey: 'propertyName',
            sortType: 'string',
            width: headerColWidth,
            header: 'Property',
            isSortable: true,
            renderer: addressColumnRenderer,
        });
        return columnConfig;
    };

    const getTableData = (coreProductInvoiceModel, isMobile) => {
        let rowList = [];
        let columnConfig = [];

        if (coreProductInvoiceModel && coreProductInvoiceModel.listingInvoices) {
            const hasLeadData = coreProductInvoiceModel.billableLeadCountAvailable;
            const hasClickData = coreProductInvoiceModel.billableClickCountAvailable;
            const hasLeaseData = coreProductInvoiceModel.leaseCountAvailable;
            const hasWaitlistData = coreProductInvoiceModel.waitlistDaysCountAvailable;
            const hasBoostData = coreProductInvoiceModel.billableBoostCountAvailable;
            const { rocketshipAvailable } = coreProductInvoiceModel;
            const hasPackageProducts = containsPackageProducts(coreProductInvoiceModel);
            rowList = getRowList(coreProductInvoiceModel, isMobile, hasLeadData, hasClickData, hasLeaseData,
                hasWaitlistData, hasBoostData);
            const containsOnlyPackageProducts = adproductsUtils
                .containsOnlyPackageProducts(coreProductInvoiceModel.listingInvoices);
            columnConfig = createColumnConfig(hasLeadData, hasClickData, hasLeaseData,
                hasWaitlistData, hasBoostData, rocketshipAvailable, hasPackageProducts, containsOnlyPackageProducts);
        }

        return {
            rowList,
            columnConfig,
        };
    };

    const { rowList, columnConfig } = getTableData(coreProductInvoiceModel, isScreenMobile);
    return (
        <div className="AllSpendTable">
            <TableMFH
                idKey="allSpend"
                columnConfig={columnConfig}
                data={isLoading ? [] : rowList}
                ErrorRowComponent={error ? () => <Error message={error} /> : undefined}
                isLoading={isLoading}
                defaultSortKey={{
                    key: 'propertyName',
                    ascending: true,
                }}
            />
            {(!isLoading && rowList.length === 0 && !error) &&
                <Error message="No properties found." />
            }
            {coreProductInvoiceModel.isEstimate && (
                <Spacer paddingY="sm">
                    <Text fontType="finePrint">
                        These numbers are an estimate. Finalized numbers will appear on your invoice.
                    </Text>
                </Spacer>
            )}
        </div>
    );
};

AllSpendTable.propTypes = propTypes;

export default AllSpendTable;
